<template>
  <div>
    <!-- Stats Cards -->
    <el-row :gutter="20" class="stats-row" v-loading="statsLoading">
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value">${{ formatCurrency(stats.total_issued) }}</div>
          <div class="stat-label">Total Issued</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value">${{ formatCurrency(stats.total_used) }}</div>
          <div class="stat-label">Total Used</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value outstanding">${{ formatCurrency(stats.outstanding_liability) }}</div>
          <div class="stat-label">Outstanding Liability</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value active-count">{{ stats.count_active }}</div>
          <div class="stat-label">Active</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value used-count">{{ stats.count_used }}</div>
          <div class="stat-label">Used</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card class="stat-card">
          <div class="stat-value expired-count">{{ stats.count_expired }}</div>
          <div class="stat-label">Expired</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Main Content -->
    <el-row>
      <el-col :span="24">
        <el-card class="data" v-loading="loading">
          <div class="data-title flex-space-between" slot="header">
            <p>Coupons</p>
            <div class="header-actions">
              <el-tooltip content="Create Coupon" placement="top">
                <el-button type="success" circle icon="el-icon-plus" @click="openCreateDialog"></el-button>
              </el-tooltip>
            </div>
          </div>

          <!-- Filters -->
          <div class="filters-row">
            <el-select v-model="statusFilter" placeholder="All Status" clearable @change="handleStatusChange" class="filter-input">
              <el-option label="All" value=""></el-option>
              <el-option label="Active" value="active"></el-option>
              <el-option label="Used" value="used"></el-option>
              <el-option label="Expired" value="expired"></el-option>
            </el-select>

            <el-input v-model="campaignFilter" placeholder="Campaign Name" clearable @input="handleCampaignChange" class="filter-input"></el-input>
            <el-input v-model="searchFilter" placeholder="Search by Code" clearable @input="handleSearchChange" class="filter-input"></el-input>
          </div>

          <!-- Table -->
          <el-table :data="coupons" :default-sort="{ prop: order_column, order: orderDirection }"
            @sort-change="handleSort" class="w100" stripe
          >
            <el-table-column prop="code" label="Code" sortable="custom" width="180">
              <template slot-scope="scope">
                <span class="code-cell">{{ scope.row.code }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="campaign_name" label="Campaign" sortable="custom"></el-table-column>
            <el-table-column prop="recipient_email" label="Recipient" sortable="custom"></el-table-column>
            <el-table-column prop="value" label="Value" sortable="custom" width="120">
              <template slot-scope="scope">
                ${{ formatCurrency(scope.row.value) }}
              </template>
            </el-table-column>
            <el-table-column prop="currency_type" label="Currency" width="100"></el-table-column>
            <el-table-column prop="status" label="Status" sortable="custom" width="100">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ capitalizeStatus(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="expires_at" label="Expires" sortable="custom" width="150">
              <template slot-scope="scope">
                {{ scope.row.expires_at | datetime }}
              </template>
            </el-table-column>
            <el-table-column prop="created_at" label="Created" sortable="custom" width="150">
              <template slot-scope="scope">
                {{ scope.row.created_at | datetime }}
              </template>
            </el-table-column>
            <el-table-column label="Actions" align="center" width="80">
              <template slot-scope="scope">
                <el-tooltip content="Delete" placement="top">
                  <el-button type="danger" circle size="small" icon="el-icon-delete" @click="handleDelete(scope.row)"></el-button>
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>

          <!-- Pagination -->
          <el-pagination
            class="float-right mt20"
            background
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page.sync="page"
            :page-sizes="[5, 10, 25, 50, 100]"
            :page-size="page_size"
            :page-count="page_count"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
          ></el-pagination>
        </el-card>
      </el-col>
    </el-row>

    <!-- Dialogs -->
    <CouponDialog />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import CouponDialog from '../components/CouponDialog.vue'

export default {
  components: {
    CouponDialog
  },

  data() {
    return {
      searchTimer: null
    }
  },

  computed: {
    ...mapState('coupons', [
      'loading',
      'statsLoading',
      'coupons',
      'stats',
      'page',
      'page_size',
      'total',
      'page_count',
      'order_column',
      'order_type',
      'statusFilter',
      'campaignFilter',
      'emailFilter',
      'searchFilter'
    ]),

    orderDirection() {
      return this.order_type === 'asc' ? 'ascending' : 'descending'
    }
  },

  methods: {
    ...mapActions('coupons', ['read', 'readStats', 'delete']),
    ...mapMutations('coupons', [
      'setPage',
      'setPageSize',
      'setOrdering',
      'setStatusFilter',
      'setCampaignFilter',
      'setEmailFilter',
      'setSearchFilter',
      'setDialog',
      'reset'
    ]),

    formatCurrency(value) {
      if (!value) return '0.00'
      const num = parseFloat(value)
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    getStatusType(status) {
      switch (status) {
        case 'active':
          return 'success'
        case 'used':
          return 'info'
        case 'expired':
          return 'danger'
        default:
          return 'info'
      }
    },

    capitalizeStatus(status) {
      if (!status) return ''
      return status.charAt(0).toUpperCase() + status.slice(1)
    },

    openCreateDialog() {
      this.setDialog(true)
    },

    handleStatusChange(value) {
      this.setStatusFilter(value)
      this.setPage(1)
      this.read()
    },

    handleCampaignChange(value) {
      this.setCampaignFilter(value)
      this.debouncedRead()
    },

    handleEmailChange(value) {
      this.setEmailFilter(value)
      this.debouncedRead()
    },

    handleSearchChange(value) {
      this.setSearchFilter(value)
      this.debouncedRead()
    },

    debouncedRead() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.setPage(1)
        this.read()
      }, 500)
    },

    handleSort(column) {
      this.setOrdering({
        column: column.prop,
        type: column.order === 'ascending' ? 'asc' : 'desc'
      })
      this.read()
    },

    handleSizeChange(size) {
      this.setPageSize(size)
      this.read()
    },

    handleCurrentChange(page) {
      this.setPage(page)
      this.read()
    },

    handleDelete(coupon) {
      this.$confirm(
        `Are you sure you want to delete coupon ${coupon.code}?`,
        'Warning',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        this.delete(coupon.id)
      })
    }
  },

  created() {
    this.readStats()
    this.read()
  },

  beforeRouteLeave(to, from, next) {
    this.reset()
    next()
  }
}
</script>

<style scoped>
.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}

.stat-value.outstanding {
  color: #e6a23c;
}

.stat-value.active-count {
  color: #67c23a;
}

.stat-value.used-count {
  color: #909399;
}

.stat-value.expired-count {
  color: #f56c6c;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 5px;
}

.data-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.data-title p {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filters-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filter-input {
  width: 200px;
}

.w100 {
  width: 100%;
}

.mt20 {
  margin-top: 20px;
}

.float-right {
  float: right;
}

.code-cell {
  font-family: monospace;
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 3px;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 5px;
}

.flex-space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
